import i from 'i18next'
import { Cart, gifts } from 'lib/hooks/use-cart'
import Image from 'components/Image'
import { motion } from 'framer-motion'
import { products as ease } from 'constants/easings'
import { currency } from 'lib/currency'

type Props = {
	cart: Cart
	which: 'first' | 'second'
	onChoose: ( variantId: string ) => void
}

export default function PickGift({ cart, which, onChoose }: Props ) {
	const current = cart[ which === 'second' ? 'freeGift2' : 'freeGift' ].gift
	const options = gifts[ which ]

	return (
		<div className="options">
			{options.map( ( gift ) => (
				<motion.a
					key={gift.id}
					whileHover="hover"
					variants={{
						initial: { opacity: 0, y: 50 },
						animate: { opacity: 1, y: 0 },
					}}
					transition={{
						opacity: { duration: 1, ease },
						default: { duration: 1, ease },
					}}
					onClick={() => onChoose( gift.id )}
				>
					<picture>
						<Image
							className="product"
							src={gift.image?.originalSrc}
							loading="lazy"
							sizes="
								(max-width: 400px) 90vw,
								(max-width: 900px) 45vw,
								(max-width: 1500px) 28.5vw,
								21.5vw
							"
							variants={{
								hover: { scale: 1.05 },
							}}
						/>
						<motion.div
							className="cta"
							initial={
								current?.variant.id !== gift.id && { opacity: 0 }
							}
							variants={{
								hover: {
									opacity: 1,
									transition: { delay: 0.2 },
								},
							}}
						>
							{current?.variant.id === gift.id
								? i.t( 'cart.gift.current' )
								: gift.availableForSale
									? i.t( 'cart.gift.choose' )
									: i.t( 'product.button.notinstockshort' )}
							{gift.availableForSale && <div className="underline" />}
						</motion.div>
					</picture>
					<div className="title">{gift.title}</div>
					<div className="price">
						{!!gift.compareAtPrice.amount && (
							<span className="from">
								{currency.format( gift.compareAtPrice.amount )}
							</span>
						)}
						{currency.format( 0 )}
					</div>
				</motion.a>
			) )}
			<style jsx>{`
				.options {
					display: grid;
					grid-template-columns: 1fr 1fr;
					grid-gap: 20px;
					padding: 20px 30px;
					overflow: auto;
				}

				picture {
					position: relative;
					padding: 0% 10% 30%;
					margin-bottom: 25px;
					background-color: #f7f7f7;
				}
				picture :global(img.product) {
					object-fit: contain;
					image-rendering: -webkit-optimize-contrast;
				}
				picture :global(img.hover) {
					position: absolute;
					top: 0;
					bottom: 0;
					left: 0;
					right: 0;
					opacity: 0;
					transition: opacity ease-in-out 0.5s;
				}
				picture :global(.cta) {
					position: absolute;
					bottom: 10%;
					left: 50%;
					white-space: nowrap;
					transform: translateX(-50%);
					text-align: center;
					text-transform: lowercase;
				}
				picture :global(.cta .underline) {
					position: absolute;
					bottom: -4px;
					left: 1px;
					right: 1px;
					height: 1px;
					background-color: currentColor;
				}

				.title,
				.price {
					font-size: 18px;
					text-transform: lowercase;
				}
				.from {
					position: relative;
					margin-right: .5em;
					color: #B8B8B8;
				}
				.from::after {
					content: '';
					border-bottom: 1px solid currentColor;
					position: absolute;
					left: 0;
					top: 52%;
					width: 100%;
				}
			`}</style>
		</div>
	)
}
